import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, CheckCircle, Mail, Star, UserPlus } from 'lucide-react';
import NpsScoreBadge from '../../components/survey/NpsScoreBadge';
import AddPromoterFromSurveyModal from '../../components/survey/AddPromoterFromSurveyModal';
import { useGetSurveyResponseDetailQuery } from '../../store/surveyApi';

const StarRating: React.FC<{ score: number }> = ({ score }) => {
  const stars = Math.round((score / 10) * 5);
  return (
    <div className="flex gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`w-5 h-5 ${i < stars ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`}
        />
      ))}
    </div>
  );
};

const SurveyResponseDetail: React.FC = () => {
  const { t } = useTranslation();
  const { responseId } = useParams<{ responseId: string }>();
  const navigate = useNavigate();
  const { data: response, isLoading } = useGetSurveyResponseDetailQuery(responseId!, { skip: !responseId });
  const [showAddPromoterModal, setShowAddPromoterModal] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-4 max-w-3xl">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  if (!response) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-500">{t('surveys.response.notFound')}</p>
        <button onClick={() => navigate('/survey')} className="mt-4 text-purple-600 underline text-sm">
          {t('surveys.response.backToSurveys')}
        </button>
      </div>
    );
  }

  const date = new Date(response.completedAt).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/survey')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-4 h-4 text-gray-600" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">{t('surveys.response.detailTitle')}</h1>
          <p className="text-sm text-gray-500">{t('surveys.response.submission', { name: response.respondentName })}</p>
        </div>
      </div>

      {/* Summary card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{response.respondentName}</h2>
            <div className="flex items-center gap-1.5 mt-1 text-sm text-gray-500">
              <Mail className="w-3.5 h-3.5" />
              <span>{response.respondentEmail}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="flex items-center gap-1.5 text-xs text-green-600 bg-green-50 border border-green-200 rounded-full px-3 py-1 font-medium">
              <CheckCircle className="w-3.5 h-3.5" />
              {t('surveys.response.completed')}
            </span>
            {response.isPromoter ? (
              <span className="flex items-center gap-1.5 text-xs text-purple-600 bg-purple-50 border border-purple-200 rounded-full px-3 py-1 font-medium">
                <UserPlus className="w-3.5 h-3.5" />
                {t('surveys.response.alreadyPromoter')}
              </span>
            ) : (
              <button
                onClick={() => setShowAddPromoterModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
              >
                <UserPlus className="w-3.5 h-3.5" />
                {t('surveys.response.addAsPromoter')}
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-6 mt-6 pt-6 border-t border-gray-100">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-2">{t('surveys.response.npsScore')}</p>
            <div className="flex items-center gap-2">
              <NpsScoreBadge score={response.npsScore} size="lg" />
              <span className="text-sm text-gray-600">{t('surveys.response.ratings')}</span>
            </div>
          </div>
          <div className="border-l border-gray-100 pl-6">
            <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-2">{t('surveys.response.completedDate')}</p>
            <div className="flex items-center gap-1.5 text-sm text-gray-700">
              <Calendar className="w-4 h-4 text-blue-500" />
              <span>{date}</span>
            </div>
          </div>
          <div className="border-l border-gray-100 pl-6">
            <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-2">{t('surveys.response.overallRating')}</p>
            <StarRating score={response.npsScore} />
          </div>
        </div>
      </div>

      {/* Individual answers */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
        <h3 className="font-semibold text-gray-900">{t('surveys.response.surveyResponses')}</h3>
        {response.answers.map((answer, index) => (
          <div key={answer.questionId} className="space-y-2">
            <p className="text-sm font-medium text-gray-700">
              {index + 1}. {answer.questionText}
            </p>
            <div className="bg-gray-50 rounded-lg px-4 py-3">
              {answer.type === 'NPS_SCALE' ? (
                <div className="flex items-center gap-2">
                  <NpsScoreBadge score={answer.npsScore ?? 0} size="sm" />
                  <span className="text-sm text-gray-700">{answer.npsScore} / 10</span>
                </div>
              ) : (
                <p className="text-sm text-gray-700">{answer.textAnswer || '—'}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Summary feedback */}
      {response.generalFeedback && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-3">{t('surveys.response.summaryFeedback')}</h3>
          <blockquote className="border-l-4 border-purple-500 pl-4 text-sm text-gray-700 italic">
            "{response.generalFeedback}"
          </blockquote>
        </div>
      )}

      {/* Action banner */}
      {!response.isPromoter && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-100 rounded-xl p-5 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-gray-900">{t('surveys.response.potentialPromoter')}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {t('surveys.response.addToNetwork', { firstName: response.respondentName.split(' ')[0] })}
            </p>
          </div>
          <button
            onClick={() => setShowAddPromoterModal(true)}
            className="flex-shrink-0 flex items-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-xl text-sm font-medium hover:bg-purple-700 transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            {t('surveys.response.addAsPromoter')}
          </button>
        </div>
      )}

      {showAddPromoterModal && (
        <AddPromoterFromSurveyModal
          respondentName={response.respondentName}
          respondentEmail={response.respondentEmail}
          onClose={() => setShowAddPromoterModal(false)}
        />
      )}
    </div>
  );
};

export default SurveyResponseDetail;
